import { ActivatedRoute, Data, Route } from '@angular/router';
import { marker as extractTranslation } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import { AppFeatureDimKey, AppFeatureDims, AppFeatureKey, AppRoleDims, AppRoleKey } from '@nk/data-entities-lib';
import { TabItem } from '@nk/ui-lib';

export type Tab = Partial<Route> &
  TabItem<{
    showOnlyAdmin?: boolean;
    showToBeta?: boolean;
    noBreadcrumb?: true;
    feature?: AppFeatureDimKey;
    roles?: AppRoleKey[];
  }>;

export interface NavigationItem {
  name: string;
  url: string;
  segment: string;
  active: boolean;
  icon: string;
  requiredFeature?: AppFeatureKey;
  requiredRole?: AppRoleKey;
  doesNotRequireBase?: boolean; // Shown even if subscription is cancelled
}

export interface Breadcrumb {
  data: Data;
  url: string;
}

export const MAIN_NAVIGATION_PATHS: NavigationItem[] = [
  {
    name: extractTranslation('common.enin.dashboard'),
    url: '/',
    segment: '/',
    active: false,
    icon: 'tachometer',
    doesNotRequireBase: true,
  },
  {
    name: extractTranslation('common.enin.watchlist'),
    url: '/watchlist',
    segment: 'watchlist',
    active: false,
    icon: 'bell',
    requiredFeature: AppFeatureKey.base,
  },
  {
    // TODO: FIX resolver for company routes to use company-browser key
    name: extractTranslation('common.enin.company_browser'),
    url: '/company',
    segment: 'company',
    active: false,
    icon: 'search',
    requiredFeature: AppFeatureKey.base,
  },
  {
    name: extractTranslation('common.enin.property'),
    url: '/property',
    segment: 'property',
    active: false,
    icon: 'home',
    requiredFeature: AppFeatureKey.norwegian_property_data,
    requiredRole: AppRoleKey.norwegian_property_data,
  },
  {
    name: extractTranslation('common.enin.screening'),
    url: '/screening',
    segment: 'screening',
    active: false,
    icon: 'binoculars',
    // feature: 'screening', // ??
    requiredFeature: AppFeatureKey.screening,
    requiredRole: AppRoleKey.aml,
  },
  {
    name: extractTranslation('common.enin.bank_account_prevalidation'),
    url: '/bank-account-prevalidation',
    segment: 'bank-account-prevalidation',
    active: false,
    icon: 'credit-card-alt',
    requiredRole: AppRoleKey.bank_account_prevalidation,
    requiredFeature: AppFeatureKey.bank_account_prevalidation,
  },
  // navigation.push({
  //   name: this._translationService.instant(extractTranslation('common.enin.collateral')) as string,
  //   url: '/collateral',
  //   segment: 'collateral',
  //   active: this.partOfSegment('collateral'),
  //   icon: 'sticky-note',
  // });
];

export function getMainNavigationPaths(
  featureDims: AppFeatureDims,
  roleDims: AppRoleDims,
  routerUrl: string,
  translationService: TranslateService,
): NavigationItem[] {
  if (!(featureDims && roleDims && routerUrl && translationService)) {
    throw new Error('featureDims, roleDims, routerUrl and translationService must be provided');
  }
  let paths = MAIN_NAVIGATION_PATHS;

  // TODO: check for base features

  // translate path names
  paths = paths.map((item) => {
    return {
      ...item,
      name: translationService.instant(item.name),
    };
  });
  if (!featureDims.flags.base) {
    // User does not have base.
    paths = paths.filter((nav) => {
      return nav.doesNotRequireBase;
    });
  }
  // filter on feature
  paths = paths.filter((x) => {
    return !(x.requiredFeature && !featureDims.keys.includes(x.requiredFeature));
  });
  // filter on role
  paths = paths.filter((x) => {
    return !(x.requiredRole && !roleDims.keys.includes(x.requiredRole));
  });
  // set active path
  paths = paths.map((x) => {
    return {
      ...x,
      active: isRouteActive(routerUrl, x.url),
    };
  });
  return paths;
}

export function getActiveTabs(tabs: Tab[], appFeature: AppFeatureDims, adminFlag: boolean, routerUrl: string): Tab[] {
  if (!tabs) {
    throw new Error('tabs must be provided');
  }
  if (!appFeature) {
    throw new Error('appFeature must be provided');
  }
  return tabs
    .filter((tab) => {
      const requiredFeature = tab.data?.feature;
      if (requiredFeature !== undefined) {
        return appFeature.flags[requiredFeature];
      }
      const showToAdmin = tab.data?.showOnlyAdmin;
      if (showToAdmin !== undefined) {
        return adminFlag;
      }
      return true;
    })
    .map((feature) => {
      return {
        ...feature,
        active: feature.url === routerUrl,
      };
    });
}

export function getFirstTabRoute(route: ActivatedRoute, url = ''): { child: ActivatedRoute; url: string } | null {
  if (!route) {
    return null;
  }
  const children = route.children;
  if (children.length === 0) {
    return null;
  }
  const [firstChild] = children;
  if (firstChild.snapshot.url.length > 0) {
    url += '/' + firstChild.snapshot.url.map((segment) => segment.path).join('/');
  }
  if (firstChild.snapshot.data['tabbed']) {
    return { child: firstChild, url };
  }
  return getFirstTabRoute(firstChild, url);
}

export function getBreadcrumbs(route: ActivatedRoute, url = '', breadcrumbs: Breadcrumb[] = []): Breadcrumb[] {
  const children: ActivatedRoute[] = route.children;
  if (children.length === 0) {
    return breadcrumbs;
  }
  const [firstChild] = children;
  url += '/' + firstChild.snapshot.url.map((part) => part.path).join('/');
  // !child.snapshot.data['tabbed']
  if (url && !route.snapshot.data.noBreadcrumb) {
    breadcrumbs.push({
      data: route.snapshot.data,
      url: url,
    });
  }
  return getBreadcrumbs(firstChild, url, breadcrumbs);
}

export function generateTabFromTabRoute(tabbedRoute: ActivatedRoute, url: string): Tab[] {
  const tabs = tabbedRoute.routeConfig.children;
  let ignoreTabs: string[] = [];
  if (tabbedRoute.snapshot.data.resolvedInfo) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
    ignoreTabs = tabbedRoute.snapshot.data.resolvedInfo.ignoreTabs;
  }
  return tabs
    .filter((tab) => {
      if (ignoreTabs?.includes(tab.path)) {
        return false;
      }
      return !tab.data.noTab;
    })
    .map((route) => {
      const tab: Tab = {
        ...route,
        url: route.path.length > 0 ? `${url}/${route.path}` : url,
        dataCy: route.path,
        data: route.data,
        active: undefined,
      };
      return tab;
    });
}

function isRouteActive(currentUrl: string, navigationPath: string): boolean {
  const pathToCheck = currentUrl.split('/')[1];
  if (navigationPath === '/company') {
    // company browser is special. Should be either at root of browser, or with a query param.
    return currentUrl === '/company' || currentUrl.includes('/company?');
  }
  if (navigationPath === '/') {
    return currentUrl === '/' || currentUrl === '/?rbc=true' || currentUrl === '/?choosing_customer=true';
  }
  if (pathToCheck) {
    return navigationPath.includes(pathToCheck);
  }
}
